import { Stack } from "@fluentui/react";
import { CascadeChildMergeActions, EMergeVMStatus, getAvailableMergeOptionsAsDropDown } from "global/Compare/ComparisonViewModelGenerator";
import { useState } from "react";
import CompareTable from "../Pharmacy/PharmacyPlan/PharmacyCompareTable";
import { TABLE_STYLES } from "../Pharmacy/PharmacyPlan/PharmacyPlan.constants";
import { ACTION_TITLE, getDefaultCollapsed, ORIGIN_TITLE, shouldIncludeComparisonItem, STACK_DEFAULT_PROPS, STICKY_HEADER_HEIGHT, TARGET_TITLE } from "./Compare.constants";
import CompareRow from "./CompareRow";
import { CompareTitle } from "./CompareTitle";
import CustomPanel, { EPanelStyleType, getPanelStyleTypeFromMergeAction } from "./CustomPanel";
import { FlexibleCompareRow } from "./FlexibleCompareRow";

import { EControlType } from "components/ControlWrapper";
import CompareMergeStatusIndicator from "./CompareMergeStatusIndicator";
import { CompareStatusIndicator } from "./CompareStatusIndicator";
import { IEntityComparePanelProps } from "./EntityComparePanel";
import MergeControl from "./MergeControl";


interface IFlexibleAttributePanelsProps {
    entityProps: IEntityComparePanelProps;
    stickyOffset: number;
}

const EntityFlexibleAttributePanel: React.FC<IFlexibleAttributePanelsProps> = (props) => {

    // for attribute set, as there could be multiple sets within a section,
    // we track merge action through local state with key of attribute set id
    // this is also backed by compareVM so that the merge action will be preserved, 
    // and this will act only to render the component when merge action changes, as compareVM is not observable
    const [_attributeSetMergeActions, setAttributeSetMergeActions] = useState<Record<string, EMergeVMStatus>>({});

    // for non-standard section, we can track merge action through local state
    // but its also backed by compareVM so that the merge action will be preserved
    // and this will act only to render the component when merge action changes, as compareVM is not observable
    const [_nonStandardMergeActions, setNonStandardMergeActions] = useState<Record<string, EMergeVMStatus>>({});

    const includeItem = (changed?: boolean) => shouldIncludeComparisonItem(props.entityProps.configuration.showDifferencesOnly, changed);

    const tabElements: JSX.Element[] = [];

    props.entityProps.compareVM.tabsVM.forEach((tab) => {

        if (!includeItem(tab.changed)) {
            return;
        }

        const sectionPanels: JSX.Element[] = [];

        tab.sectionsVM.forEach((section) => {
            if (!includeItem(section.changed)) {
                return;
            }

            // Attributes
            // -----------------------------------------------------------------------
            const sectionElements: JSX.Element[] = [];

            section.attributesVM.forEach((eachAttribute) => {

                if (!includeItem(eachAttribute.changed)) {
                    return;
                }

                // skip message type attributes, as they are not editable and do not have a value to compare
                if (eachAttribute.attributeDefinition.bcbs_config_masterattribute.bcbs_attributetypecode === EControlType.message) {
                    return;
                }

                // const onComment = props.entityProps.isUnderwritingApp
                //     ? buildRowCommentHandler(
                //         props.entityProps.onSetCommentInfo,
                //         tab.tabDefinition.tab.bcbs_config_tabid,
                //         section.sectionDefinition.section.bcbs_config_sectionid,
                //         eachAttribute.attributeDefinition.bcbs_config_attribute.bcbs_config_attributeid,
                //         eachAttribute.attributeDefinition.bcbs_config_attribute.bcbs_label,
                //         eachAttribute.attributeDefinition.bcbs_config_attribute.bcbs_name,
                //     )
                //     : undefined

                // eachAttributeVM.values
                sectionElements.push(
                    <FlexibleCompareRow
                        key={eachAttribute.key}
                        entityProps={props.entityProps}
                        attribute={eachAttribute}
                    />
                );
            });

            // Attributeset
            // -----------------------------------------------------------------------
            const attributeSetPanels: JSX.Element[] = [];

            section.attributeSetsVM.forEach((eachMasterAttSet) => {
                eachMasterAttSet.attributeSets.forEach((eachMasterAttributeSet, index) => {
                    if (!includeItem(eachMasterAttributeSet.changed)) {
                        return;
                    }

                    const attributeSetMergeKey = eachMasterAttributeSet.id;
                    // const selectedAttributeSetMergeAction = eachMasterAttributeSet.mergeAction ?? attributeSetMergeActions[attributeSetMergeKey] ?? EMergeAction.EAsIs;

                    const attributeSetElements: JSX.Element[] = [];

                    // Attributeset - Properties
                    // -----------------------------------------------------------------------
                    eachMasterAttributeSet.propertiesVM.rowsVM.forEach((eachProperty) => {
                        if (!includeItem(eachProperty.changed)) {
                            return;
                        }

                        attributeSetElements.push(
                            <CompareRow
                                key={eachProperty.propertyDefinition.logicalName}
                                data-property={eachProperty.propertyDefinition.name}
                                name={<CompareTitle
                                    name={eachProperty.propertyDefinition.name}
                                    changed={eachProperty.changed}
                                    entityProps={props.entityProps}
                                />}
                                valueVM={eachProperty}
                                entityProps={props.entityProps}
                            />
                        );
                    });

                    // Attributeset - Attributes
                    // -----------------------------------------------------------------------
                    eachMasterAttributeSet.attributesVM.forEach((eachAttributeSetAttVM) => {

                        if (!includeItem(eachAttributeSetAttVM.changed)) {
                            return;
                        }

                        // skip message type attributes, as they are not editable and do not have a value to compare
                        if (eachAttributeSetAttVM.attributeDefinition.bcbs_config_masterattribute.bcbs_attributetypecode === EControlType.message) {
                            return;
                        }

                        attributeSetElements.push(
                            <FlexibleCompareRow
                                key={eachAttributeSetAttVM.key}
                                entityProps={props.entityProps}
                                attribute={eachAttributeSetAttVM}
                            />
                        );
                    });

                    attributeSetPanels.push(
                        <CustomPanel
                            id={`${props.entityProps.compareVM.uniqueId}-${eachMasterAttributeSet.definition.bcbs_config_masterattribute.bcbs_config_masterattributeid}-${index}`}
                            key={`${eachMasterAttributeSet.definition.bcbs_config_masterattribute.bcbs_config_masterattributeid}-${index}`}
                            data-attribute-set-id={eachMasterAttributeSet.id}
                            data-attribute-set-config-master-attribute-id={eachMasterAttributeSet.definition.bcbs_config_masterattribute.bcbs_config_masterattributeid}
                            title={<CompareTitle
                                name={`AttributeSet → ${eachMasterAttributeSet.definition.bcbs_config_attribute.bcbs_label} → ${eachMasterAttributeSet.name}`}
                                changed={eachMasterAttributeSet.changed}
                                entityProps={props.entityProps}
                            />}
                            headerRight={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <CompareMergeStatusIndicator
                                        mergeStatus={eachMasterAttributeSet.mergeAction}
                                        compareStatus={eachMasterAttributeSet.compareResult}
                                        isMergeApp={props.entityProps.isMergeApp}
                                    />
                                    <CompareStatusIndicator
                                        compareStatus={eachMasterAttributeSet.compareResult}
                                    />
                                </div>
                            }
                            defaultCollapsed={false}
                            canCollapse={true}
                            stickyHeader={true}
                            stickyTop={props.stickyOffset + (STICKY_HEADER_HEIGHT * 3)}
                            stickyZIndex={300}
                            styleType={getPanelStyleTypeFromMergeAction(props.entityProps.isMergeApp, eachMasterAttributeSet.mergeAction)}
                        >
                            {props.entityProps.isMergeApp && !props.entityProps.mergeDisabled && (
                                <MergeControl
                                    mergeOptions={getAvailableMergeOptionsAsDropDown(eachMasterAttributeSet.compareResult, { isAttributeSet: true })}
                                    selectedMergeOption={eachMasterAttributeSet.mergeAction}
                                    entityProps={props.entityProps}
                                    entityVM={eachMasterAttributeSet}
                                    onChange={(event, option) => {
                                        if (!option || option.data === undefined)
                                            return;

                                        CascadeChildMergeActions(
                                            option.data,
                                            props.entityProps.configuration.defaultMergeAction,
                                            null,
                                            eachMasterAttributeSet,
                                            null,
                                        );

                                        setAttributeSetMergeActions((current) => ({
                                            ...current,
                                            [attributeSetMergeKey]: eachMasterAttributeSet.mergeAction,
                                        }));
                                    }}
                                />
                            )}

                            <CompareTable
                                className={props.entityProps.classNames}
                                tableStyles={TABLE_STYLES}
                                originHeader={ORIGIN_TITLE}
                                targetHeader={TARGET_TITLE}
                                actionHeader={ACTION_TITLE}
                                hideActionHeader={!(props.entityProps.isUnderwritingApp || props.entityProps.isMergeApp)}
                            >
                                {attributeSetElements}
                            </CompareTable>
                        </CustomPanel>
                    );
                });
            });

            const nonStandardPanels: JSX.Element[] = [];

            // Nonstandard
            // -----------------------------------------------------------------------
            section.nonStandardsVM.forEach((nonStandardVM, nonStandardIndex) => {

                if (!includeItem(nonStandardVM.changed)) {
                    return;
                }

                const nonStandardElements: JSX.Element[] = [];

                // Nonstandard - Properties
                // -----------------------------------------------------------------------
                nonStandardVM.propertiesVM.rowsVM.forEach((property) => {
                    if (!includeItem(property.changed)) {
                        return;
                    }

                    nonStandardElements.push(
                        <CompareRow
                            key={property.propertyDefinition.logicalName}
                            data-property={'true'}
                            name={<CompareTitle
                                changed={property.changed}
                                name={property.propertyDefinition.name}
                                entityProps={props.entityProps}
                            />}
                            valueVM={{
                                origin: property.origin,
                                target: property.target,
                                changed: property.changed,
                                mergeAction: property.mergeAction,
                            }}
                            entityProps={props.entityProps}
                        />
                    );
                });

                // Nonstandard - Attributes
                // -----------------------------------------------------------------------
                nonStandardVM.attributesVM.forEach((eachNonStandardAttributeVM) => {

                    if (!includeItem(eachNonStandardAttributeVM.changed)) {
                        return;
                    }

                    // skip message type attributes, as they are not editable and do not have a value to compare
                    if (eachNonStandardAttributeVM.attributeDefinition.bcbs_config_masterattribute.bcbs_attributetypecode === EControlType.message) {
                        return;
                    }

                    nonStandardElements.push(
                        <FlexibleCompareRow
                            key={eachNonStandardAttributeVM.key}
                            attribute={eachNonStandardAttributeVM}
                            entityProps={props.entityProps}
                        />
                    );
                });

                const nonStandardMergeKey = nonStandardVM.id;

                nonStandardPanels.push(
                    <CustomPanel
                        key={`${section.sectionDefinition.section.bcbs_config_sectionid}-nonstandard-${nonStandardIndex}`}
                        id={`${props.entityProps.compareVM.uniqueId}-${section.sectionDefinition.section.bcbs_config_sectionid}-nonstandard-${nonStandardIndex}`}
                        data-nonstandard-id={nonStandardVM.id}
                        data-nonstandard-section-id={nonStandardVM.sectionDefinition.section.bcbs_config_sectionid}
                        title={<CompareTitle
                            name={`Non-Standard → ${nonStandardVM.name}`}
                            changed={nonStandardVM.changed}
                            entityProps={props.entityProps}
                        />}
                        headerRight={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CompareMergeStatusIndicator
                                    mergeStatus={nonStandardVM.mergeAction}
                                    compareStatus={nonStandardVM.compareResult}
                                    isMergeApp={props.entityProps.isMergeApp}
                                />
                                <CompareStatusIndicator
                                    compareStatus={nonStandardVM.compareResult}
                                />
                            </div>
                        }
                        defaultCollapsed={false}
                        canCollapse={true}
                        stickyHeader={true}
                        stickyTop={props.stickyOffset + (STICKY_HEADER_HEIGHT * 3)}
                        stickyZIndex={300}
                        styleType={getPanelStyleTypeFromMergeAction(props.entityProps.isMergeApp, nonStandardVM.mergeAction)}
                    >
                        {props.entityProps.isMergeApp && !props.entityProps.mergeDisabled && (
                            <MergeControl
                                mergeOptions={getAvailableMergeOptionsAsDropDown(nonStandardVM.compareResult, { isNonStandard: true })}
                                selectedMergeOption={nonStandardVM.mergeAction}
                                entityProps={props.entityProps}
                                entityVM={nonStandardVM}
                                onChange={(event, option) => {
                                    if (!option || option.data === undefined)
                                        return;

                                    CascadeChildMergeActions(
                                        option.data,
                                        props.entityProps.configuration.defaultMergeAction,
                                        null,
                                        null,
                                        nonStandardVM,
                                    );

                                    setNonStandardMergeActions((current) => ({
                                        ...current,
                                        [nonStandardMergeKey]: nonStandardVM.mergeAction,
                                    }));
                                }}
                            />
                        )}

                        <CompareTable
                            className={props.entityProps.classNames}
                            tableStyles={TABLE_STYLES}
                            originHeader={ORIGIN_TITLE}
                            targetHeader={TARGET_TITLE}
                            actionHeader={ACTION_TITLE}
                            hideActionHeader={!(props.entityProps.isUnderwritingApp || props.entityProps.isMergeApp)}
                        >
                            {nonStandardElements}
                        </CompareTable>
                    </CustomPanel>
                );
            });

            sectionPanels.push(
                <CustomPanel
                    id={`${props.entityProps.compareVM.uniqueId}-${section.sectionDefinition.section.bcbs_config_sectionid}`}
                    key={section.sectionDefinition.section.bcbs_config_sectionid}
                    data-section-id={section.sectionDefinition.section.bcbs_config_sectionid}
                    title={<CompareTitle
                        name={section.sectionDefinition.section.bcbs_name}
                        changed={section.changed}
                        entityProps={props.entityProps}
                    />}
                    canCollapse={true}
                    defaultCollapsed={getDefaultCollapsed(props.entityProps.configuration, section.changed)}
                    stickyHeader={true}
                    stickyTop={props.stickyOffset + (STICKY_HEADER_HEIGHT * 2)}
                    stickyZIndex={250}
                    styleType={EPanelStyleType.Default}

                >
                    {sectionElements.length !== 0 && (
                        <CompareTable
                            className={props.entityProps.classNames}
                            tableStyles={TABLE_STYLES}
                            originHeader={ORIGIN_TITLE}
                            targetHeader={TARGET_TITLE}
                            actionHeader={ACTION_TITLE}
                            hideActionHeader={!(props.entityProps.isUnderwritingApp || props.entityProps.isMergeApp)}
                        >
                            {sectionElements}
                        </CompareTable>
                    )}

                    <Stack {...STACK_DEFAULT_PROPS}>
                        {attributeSetPanels}
                        {nonStandardPanels}
                    </Stack>

                </CustomPanel>
            );
        });

        tabElements.push(
            <CustomPanel
                id={`${props.entityProps.compareVM.uniqueId}-${tab.tabDefinition.tab.bcbs_config_tabid!}`}
                key={`${props.entityProps.compareVM.uniqueId}-${tab.tabDefinition.tab.bcbs_config_tabid!}`}
                data-tab-id={tab.tabDefinition.tab.bcbs_config_tabid!}
                title={<CompareTitle
                    name={tab.tabDefinition.tab.bcbs_name}
                    changed={tab.changed}
                    entityProps={props.entityProps}
                />}
                defaultCollapsed={getDefaultCollapsed(props.entityProps.configuration, tab.changed)}
                canCollapse={true}
                stickyHeader={true}
                stickyTop={props.stickyOffset + STICKY_HEADER_HEIGHT}
                stickyZIndex={200}
                styleType={EPanelStyleType.Default}
            >
                <Stack {...STACK_DEFAULT_PROPS}>
                    {sectionPanels}
                </Stack>
            </CustomPanel>
        );
    });

    return (
        <Stack {...STACK_DEFAULT_PROPS}>
            {tabElements}
        </Stack>
    );
};

export default EntityFlexibleAttributePanel;